import { createQueryKeys } from '@lukemorales/query-key-factory'
import AivatarClient from '../AivatarClient'

// ── 단건결제(크레딧 팩) ────────────────────────────────────────────────
// BE: aivatar PR #538 (T-C1) · #543 (terms). 계약은 aipark-front #279 코멘트.
// 권한: 판매 목록 · 결과 조회는 워크스페이스 구성원, 주문 생성 · 확정은 소유자만(구성원은 403).

export type CreditPack = {
  id: number
  name: string
  /** 부가세 포함 결제 금액(D-85). 화면은 부가세 별도로 표기하므로 ÷ 1.1 로 보인다 */
  amount: number
  /** 지급 크레딧 */
  creditAmount: number
  currencyCode: string
}

/**
 * 고지 문안의 숫자 자리(요금 기준표 값). 서버가 지급 · 환불 산정 · 소멸 안내에 실제로 쓰는 설정이다.
 * 칸이 null 이면 서버에 설정이 없는 것이라, 숫자를 지어내지 말고 그 고지를 띄우지 않는다.
 */
export type CreditPackTerms = {
  /** 소멸 기한(개월, 달력 기준). 12 → "1년" */
  expiryMonths: number | null
  /** 소멸 임박 안내 시점(일 전), 큰 값부터. [0] 첫 안내 · [1] 두 번째 안내 */
  noticeDays: number[] | null
  /** 청약철회 기간(일) */
  withdrawalPeriodDays: number | null
  /** 선택권 기간 — 구독 종료일로부터 일(P5 · P6) */
  optionPeriodDays: number | null
  /** 사용률 경계(0~1). 이 값 이하가 낮은 위약금 구간 */
  lowUsageThreshold: number | null
  /** 낮은 위약금율(0~1) */
  lowUsageRate: number | null
  /** 높은 위약금율(0~1) */
  highUsageRate: number | null
}

export type CreditPackCatalog = {
  /** 거짓이면 구매 진입점을 숨긴다(비활성 표시 아님). 결제 실패 유예 중에도 참이다 */
  canPurchase: boolean
  reason: 'ELIGIBLE' | 'NOT_SUBSCRIBED' | 'NO_RECURRING_BILLING'
  /** 판매 중인 팩, 가격 오름차순 */
  packs: CreditPack[]
  /** aivatar #543 배포 전에는 없다 */
  terms?: CreditPackTerms | null
}

export type CreateCreditPackOrderResponse = {
  merchantUid: string
  orderName: string
  /** 부가세 포함 결제 금액. 결제창에 그대로 넘긴다 — 화면에서 계산하지 않는다 */
  amount: number
  currencyCode: string
}

/** 주문 생성 거절(400) 본문 */
export type CreditPackOrderError = {
  code: 'NOT_SUBSCRIBED' | 'NO_RECURRING_BILLING' | 'PACK_UNAVAILABLE' | string
  message: string
}

/**
 * 결제 결과. 네 결과 모두 HTTP 200 이며 화면은 이 result 로만 정한다(결제창 콜백의 성공/실패로 정하지 않는다).
 * - PAID 구매 완료 · FAILED 결제 실패 · CANCELLED 금액 불일치 자동 취소 · PENDING 확인 중(조회 API 로 다시 묻는다)
 */
export type CreditPackOrderResult = {
  result: 'PAID' | 'FAILED' | 'CANCELLED' | 'PENDING'
  merchantUid: string
  /** PAID 일 때만. 지급 크레딧 */
  grantedCredit: number | null
  /** PAID 일 때만. 결제 금액(부가세 포함) */
  amount: number | null
  /** PAID 일 때만. 사용 기한(그날까지, yyyy-MM-dd) */
  expiresOn: string | null
  /** 보유 크레딧 — 확정 직후면 지급 후 잔액 */
  availableCredit: number | null
  /** FAILED 일 때만. 결제대행사 메시지 그대로 */
  failMessage: string | null
}

export async function getCreditPackCatalog(workspaceId: number): Promise<CreditPackCatalog> {
  const response = await AivatarClient().get(`/workspaces/${workspaceId}/credit-packs`)
  return response.data
}

export async function createCreditPackOrder(request: {
  workspaceId: number
  packId: number
}): Promise<CreateCreditPackOrderResponse> {
  const response = await AivatarClient().post(`/workspaces/${request.workspaceId}/credit-pack-orders`, {
    packId: request.packId,
  })
  return response.data
}

/**
 * 결제 확정. 결제창이 닫히면 성공이든 실패든(사용자 취소 포함) 항상 부른다 — 취소는 웹훅이 오지 않아
 * 확정을 안 부르면 실패 사유를 받을 수 없다. 다시 불러도 지급은 한 번이다(모바일 새로고침 포함).
 */
export async function confirmCreditPackOrder(request: {
  workspaceId: number
  merchantUid: string
  impUid: string
}): Promise<CreditPackOrderResult> {
  const { workspaceId, merchantUid, impUid } = request
  const response = await AivatarClient().post(
    `/workspaces/${workspaceId}/credit-pack-orders/${encodeURIComponent(merchantUid)}/confirm`,
    { impUid },
  )
  return response.data
}

/** 결제 결과 조회 — 결제대행사를 부르지 않는다. 확인 중(PENDING) 폴링용 */
export async function getCreditPackOrder(workspaceId: number, merchantUid: string): Promise<CreditPackOrderResult> {
  const response = await AivatarClient().get(
    `/workspaces/${workspaceId}/credit-pack-orders/${encodeURIComponent(merchantUid)}`,
  )
  return response.data
}

/**
 * 구매 건별 잔액 한 줄 — "{구매일} 구매 · 남은 크레딧 {잔여량} · 사용 기한 {소멸일}까지" (BE aivatar #539 · 화면 전달본 P6)
 */
export type CreditPackLot = {
  paymentOrderId: number
  /** 결제 때 주문명(예: 크레딧 팩 3,000) */
  orderName: string
  /** 구매일(yyyy-MM-dd) */
  purchasedOn: string
  /** 사용 기한 — 이 날 당일까지(yyyy-MM-dd) */
  expiresOn: string
  /** 남은 크레딧 */
  remaining: number
}

export type CreditPackBalances = {
  /** 잔액을 믿을 수 있는가. 거짓이면 lots 가 비어 있다 — 화면은 줄을 그리지 않는다 */
  available: boolean
  /** 소멸하지 않고 남은 크레딧이 있는 결제 건만, 소멸일 이른 순(같으면 구매 이른 순) */
  lots: CreditPackLot[]
  /**
   * 구독 종료일(정책 16.5 {구독 종료일}, YYYY-MM-DD) — 선택권 기간을 이 날로 센다. 유료 구독 중이면 null(종료 뒤 무료 승급 중에는 원래 종료일).
   * 현재 구독 행의 startedAt 과 다를 수 있다(무료 승급이 회수된 경우 원래 종료일, 정책 10.4). aivatar #552 부터 온다
   */
  terminatedOn?: string | null
}

/** 크레딧 팩 구매 건별 잔액 — 워크스페이스 구성원. 환불 산정 · 소멸 임박 안내와 같은 계산이다 */
export async function getCreditPackBalances(workspaceId: number): Promise<CreditPackBalances> {
  const response = await AivatarClient().get(`/workspaces/${workspaceId}/credit-pack-balances`)
  return response.data
}

// ── 구독(정기결제) ────────────────────────────────────────────────────
// interval 은 결제 주기(1=월, 12=연)다. 미지정이면 BE 가 현재 구독의 주기를 유지한다.
//
// 주기 선택 UI 가 있는 진입점(플랜 모달)은 1·12 를 항상 명시한다. 월간을 "생략"으로
// 표현하면 BE 가 "현재 주기 유지"로 읽어 연간 구독자의 연 → 월 전환이 성립하지 않는다.
// 대신 그 토글은 반드시 현재 주기에서 시작해야 한다 — 월간으로 열어두면 등급만 바꾸려던
// 조작이 "연 → 월"로 나간다 (BE aivatar #357 T-D2). 둘은 한 쌍이라 한쪽만 바꾸면 안 된다.
//
// 주기 선택 UI 가 없는 진입점(PlanItem)은 미지정으로 두어 현재 주기를 잇는다.

export type CheckoutDetails = {
  current: SubscriptionSummary
  next: SubscriptionSummary
  refund: number
  /**
   * 이번 회 실제 청구액. next.price(주기 전체 정가)와 다를 수 있다.
   * 연간 약정의 즉시 전환에서 미도래 달 선결제분이 차감된 값이라, 결제 버튼에 붙일
   * 금액은 언제나 이쪽이다.
   */
  chargeAmount: number
}

export type SubscriptionSummary = {
  subscriptionId: number
  plan: any
  price: number
  intervalMonth: number | null
  status: string
  startedAt: string
  endAt: string
  subscribedAt: string | null
}

export async function getPaymentsCheckoutDetails(reqBody: {
  planId: string
  workspaceId: string
  interval?: number
}): Promise<CheckoutDetails> {
  const response = await AivatarClient().post(`/payments/checkout`, reqBody)

  return response.data
}

/** 연간 갱신 사전 고지 이력 (BE aivatar #357 T-D5) */
export async function getRenewalNotices(workspaceId: number) {
  const response = await AivatarClient().get(`/workspaces/${workspaceId}/subscription/renewal-notices`)

  return response.data as Array<{
    id: number
    cycleEndAt: string
    renewAt: string
    noticeDays: number
    daysBeforeRenewal: number
    planName: string
    price: number
    intervalMonth: number
    notifiedAt: string
    acknowledged: boolean
  }>
}

export async function unsubscribe(workspaceId: number) {
  const response = await AivatarClient().post(`/workspaces/${workspaceId}/unsubscribe`, {
    workspaceId,
    cancel: false, // false면 구독해지. 그렇다면 true는?
  })

  return response.data
}

export async function continueSubscribe(workspaceId: number) {
  const response = await AivatarClient().post(`/workspaces/${workspaceId}/continueSubscribe`)
  return response.data
}

/**
 * 플랜 변경 예약 취소 (BE aivatar #484, 정책 11.6·11.8).
 *
 * continueSubscribe 는 해지 예약(PAUSE) 전용이라 CHANGED 를 되돌리지 못하고 400 이다.
 * 연→월·다운그레이드·결합 예약은 이쪽이며, 아직 적용되지 않은 미래 약정이라 정산·환불이 없다.
 * 결합 예약은 한 행이라 한 번에 사라진다.
 */
export async function cancelScheduledChange(workspaceId: number) {
  const response = await AivatarClient().post(
    `/workspaces/${workspaceId}/subscription/scheduled-change/cancel`,
  )
  return response.data
}

export async function upgradeSubscribe(request: {
  workspaceId: number
  paymentMethodId: number
  planId: number
  /** 월→연 전환의 메인 경로다. 이 필드가 없으면 등록된 카드로는 연간 전환이 불가능하다 */
  interval?: number
}) {
  const { ...reqBody } = request

  const response = await AivatarClient().post(`/workspaces/${request.workspaceId}/upgradeSubscribe`, reqBody)

  return response.data
}

export async function downgradeSubscribe(request: {
  workspaceId: number
  paymentMethodId: number
  planId: number
  period?: number
  interval?: number
}) {
  const { workspaceId, ...reqBody } = request

  const response = await AivatarClient().post(`/workspaces/${workspaceId}/downgradeSubscribe`, reqBody)

  return response.data
}

export const paymentsQueries = createQueryKeys('payments', {
  checkout: (reqBody) => ({
    queryKey: [reqBody],
    queryFn: () => getPaymentsCheckoutDetails(reqBody),
  }),
  creditPacks: (workspaceId: number) => ({
    queryKey: [workspaceId],
    queryFn: () => getCreditPackCatalog(workspaceId),
  }),
  creditPackBalances: (workspaceId: number) => ({
    queryKey: [workspaceId],
    queryFn: () => getCreditPackBalances(workspaceId),
  }),
  renewalNotices: (workspaceId: number) => ({
    queryKey: [workspaceId],
    queryFn: () => getRenewalNotices(workspaceId),
  }),
})
